import { useEffect, useMemo, useState } from "react";
import {
  Button,
  cn,
  CurrencyInput,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  V2Loader,
} from "web-design-system";
import { AmcLogo } from "web-core";
import { useSchemeFolioHoldingsData, useSchemes } from "creso-core/hooks";
import { Scheme, Client } from "creso-core/types";
import { GiftAmc } from "../constants";
import { GiftFlowHeader } from "./gift-flow-header";

export interface GiftSchemeAmountStepProps {
  amc: GiftAmc;
  folioNumber: string;
  onBack: () => void;
  onProceed: (data: {
    scheme: Scheme<"all">;
    folioNumber: string;
    amount: number;
  }) => void;
  client: Client;
}

export function GiftSchemeAmountStep({
  amc,
  folioNumber,
  onBack,
  onProceed,
  client,
}: GiftSchemeAmountStepProps) {
  const { schemes, isLoading: isLoadingSchemes } = useSchemes({
    type: "all",
    product_type: "GIFT",
    amc_id: amc.amc_id,
  });
  const [selectedScheme, setSelectedScheme] = useState<Scheme<"all">>();
  const [amount, setAmount] = useState(amc.minAmount);

  const { data: holdings, isLoading: isLoadingHoldings } =
    useSchemeFolioHoldingsData({
      investmentAccountGuid: client.investment_account ?? "",
    });

  const hasNonZeroFolio = useMemo(
    () =>
      holdings?.some(
        (h) =>
          h.folio_number === folioNumber &&
          selectedScheme?.scheme_isin === h.scheme_isin
      ),
    [folioNumber, holdings, selectedScheme]
  );

  const minAmount = hasNonZeroFolio ? amc.minAmount : amc.firstOrderMinAmount;

  useEffect(() => setAmount(minAmount), [minAmount]);

  const isAmountValid = amount >= minAmount;

  return (
    <div className="flex flex-col h-full min-h-0">
      <GiftFlowHeader
        title="Gift Outbound Transaction"
        step={2}
        totalSteps={3}
        onBack={onBack}
      />
      <V2Loader
        isLoading={isLoadingSchemes || isLoadingHoldings}
        className="flex flex-col h-full min-h-0"
      >
        <div className="flex-1 flex flex-col min-h-0 overflow-y-auto pt-4">
          <span className="text-sm font-semibold text-foreground">
            Select Scheme
          </span>
          <div className="mt-3 flex flex-col gap-3">
            {schemes?.map((scheme) => (
              <button
                key={scheme.fund_scheme_id}
                type="button"
                onClick={() => setSelectedScheme(scheme)}
                className={cn(
                  "flex w-full items-center gap-3 rounded-xl border p-3 text-left transition-colors",
                  selectedScheme?.fund_scheme_id === scheme.fund_scheme_id
                    ? "border-foreground"
                    : "border-border hover:border-foreground/40"
                )}
              >
                <AmcLogo amc_code={amc.ms_code} />
                <span
                  className={cn(
                    "text-foreground text-sm",
                    selectedScheme?.fund_scheme_id === scheme.fund_scheme_id &&
                      "font-semibold"
                  )}
                >
                  {scheme.scheme_name}
                </span>
              </button>
            ))}
          </div>

          <div className="mt-auto border-t border-border pt-6 flex items-center justify-between gap-4">
            <span className="font-semibold text-sm text-foreground">
              Select Folio
            </span>
            <Select value={folioNumber} disabled>
              <SelectTrigger className="w-48">
                <SelectValue placeholder="Select folio" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={folioNumber}>{folioNumber}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="mt-6 flex items-center justify-between gap-4 border-t border-border pt-6">
            <div>
              <span className="font-semibold text-sm text-foreground">
                Amount (USD)
              </span>
              <p
                className={cn(
                  "text-xs",
                  isAmountValid ? "text-muted-foreground" : "text-destructive"
                )}
              >
                Min ${minAmount.toLocaleString()}
              </p>
            </div>
            <CurrencyInput
              className="w-32 mr-1"
              value={amount}
              onValueChange={(value) => setAmount(Number(value) || 0)}
              prefix="$"
              placeholder="$ Amount"
              intlConfig={{ locale: "en-IN", currency: "USD" }}
            />
          </div>
        </div>

        <div className="pt-6">
          <Button
            className="w-full"
            disabled={!selectedScheme || !isAmountValid}
            onClick={() =>
              selectedScheme &&
              onProceed({ scheme: selectedScheme, folioNumber, amount })
            }
          >
            Proceed
          </Button>
        </div>
      </V2Loader>
    </div>
  );
}
